import { User, Profile, Filters, EligibleUser, Coupon } from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL;

class ApiService {
  private async fetchWithAuth(endpoint: string, options: RequestInit = {}) {
    const token = localStorage.getItem('authToken');
    
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token ? `Bearer ${token}` : '',
        ...options.headers,
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'An error occurred');
    }

    return response.json();
  }

  private async fetchWithAdminAuth(endpoint: string, options: RequestInit = {}) {
    const adminToken = localStorage.getItem('adminToken');
    
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': adminToken ? `Bearer ${adminToken}` : '',
        ...options.headers,
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'An error occurred');
    }

    return response.json();
  }

  private async uploadFile(endpoint: string, formData: FormData) {
    const token = localStorage.getItem('authToken');
    
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      headers: {
        'Authorization': token ? `Bearer ${token}` : '',
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Upload failed');
    }

    return response.json();
  }

  // Auth APIs
  async register(userData: any) {
    return this.fetchWithAuth('/auth/register.php', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  async login(email: string, password: string) {
    return this.fetchWithAuth('/auth/login.php', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  async verifyEmail(token: string) {
    return this.fetchWithAuth('/auth/verify-email.php', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  }

  async verifyPhone(userId: string, code: string) {
    return this.fetchWithAuth('/auth/verify-phone.php', {
      method: 'POST',
      body: JSON.stringify({ userId, code }),
    });
  }

  async resendOTP(userId: string) {
    return this.fetchWithAuth('/auth/resend-otp.php', {
      method: 'POST',
      body: JSON.stringify({ userId }),
    });
  }

  // Profile APIs
  async getProfiles(filters: Filters, page: number = 1) {
    const params = new URLSearchParams({ 
      ...filters as any, 
      page: page.toString() 
    });
    return this.fetchWithAuth(`/profiles/list.php?${params}`);
  }

  async getProfile(id: string) {
    return this.fetchWithAuth(`/profiles/get.php?id=${id}`);
  }

  async getMyProfile() {
    return this.fetchWithAuth('/profiles/my-profile.php');
  }

  async updateProfile(profileData: Partial<Profile>) {
    return this.fetchWithAuth('/profiles/update.php', {
      method: 'POST',
      body: JSON.stringify(profileData),
    });
  }

  async toggleProfileStatus(isActive: boolean) {
    return this.fetchWithAuth('/profiles/toggle-status.php', {
      method: 'POST',
      body: JSON.stringify({ status: isActive ? 'online' : 'offline' }),
    });
  }

  async deleteProfile() {
    return this.fetchWithAuth('/profiles/delete.php', {
      method: 'DELETE',
    });
  }

  // File Upload APIs
  async uploadPhotos(files: File[]) {
    const formData = new FormData();
    files.forEach((file, index) => {
      formData.append(`photos[${index}]`, file);
    });
    return this.uploadFile('/files/upload-photos.php', formData);
  }

  async uploadDocument(file: File, type: 'aadhaar' | 'pan') {
    const formData = new FormData();
    formData.append('document', file);
    formData.append('type', type);
    return this.uploadFile('/files/upload-document.php', formData);
  }

  // Coupon APIs
  async validateCoupon(code: string) {
    return this.fetchWithAuth('/coupons/validate.php', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  }

  async checkEligibleUser(email: string, phone: string) {
    return this.fetchWithAuth('/eligible/check.php', {
      method: 'POST',
      body: JSON.stringify({ email, phone }),
    });
  }

  // Admin APIs
  async adminLogin(username: string, password: string) {
    return this.fetchWithAuth('/admin/login.php', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });
  }

  async getPendingVerifications() {
    return this.fetchWithAdminAuth('/admin/pending-verifications.php');
  }

  async verifyUser(userId: string, action: 'approve' | 'reject', notes?: string) {
    return this.fetchWithAdminAuth('/admin/verify-user.php', {
      method: 'POST',
      body: JSON.stringify({ userId, action, notes }),
    });
  }

  async addEligibleUsers(users: { email: string; phone: string }[]) {
    return this.fetchWithAdminAuth('/admin/add-eligible-users.php', {
      method: 'POST',
      body: JSON.stringify({ users }),
    });
  }

  async createCoupon(couponData: Partial<Coupon>) {
    return this.fetchWithAdminAuth('/admin/create-coupon.php', {
      method: 'POST',
      body: JSON.stringify(couponData),
    });
  }

  async getDashboardStats() {
    return this.fetchWithAdminAuth('/admin/dashboard-stats.php');
  }

  // Utility APIs
  async getCities(state: string) {
    return this.fetchWithAuth(`/utils/cities.php?state=${state}`);
  }

  async reportProfile(profileId: string, reason: string, description?: string) {
    return this.fetchWithAuth('/profiles/report.php', {
      method: 'POST',
      body: JSON.stringify({ profileId, reason, description }),
    });
  }
}

export default new ApiService();